import {
    cmeLicencesURL,
    getLicenseManagementReportURL,
    getLicenceSettingsURL,
    LicenseManagementReportQueryParams,
    LicenseManagementReportType,
    licenceReloadURL,
    licenceStatusURL,
    telSysLicencesURL
} from '../../../../NonReact/constants/ApiConstants'
import { AppLicenceDataList, LicenceValue, LicenceValues, TelSysProfile } from '../../Types/Types'
import { requestGetJSON } from '../../../../NonReact/common/API/REST/RESTAPIClient'
import { LicenceResponse, LicenceResponseMapped } from '../../../../NonReact/Types/Types'
import { APIData, JSONResponse } from '../../../../NonReact/common/Types/Types'
import { requestJSON } from '../../../../NonReact/common/API/REST/RESTClient'
import { RESTMethods } from '../../../../NonReact/constants/REST'
import { LicenseManagementStatusResponse } from '../../../../NonReact/Types/LicenseManagementTypes'

export const TEL_SYS_PROFILE_TYPE_ID: string = 'TelSysTypeIDDefaultTelSrvs'
export const REQUIRED_LICENCE_VALUES: string[] = [
    'BillingTagDefaultTelSrvs',
    'BillingTagDisableTelSrvs',
    'FeatureCapsCallRecIntegration'
]

const LICENCE_FILE_CACHE_TIME: number = 10000
const LICENCE_SUMMARY_CACHE_TIME: number = 1000
const LICENCE_MANAGEMENT_STATUS_CACHE_TIME: number = 0

// Licence Settings API
export const getLicenceSettings = (): Promise<Nullable<AppLicenceDataList>> =>
    requestGetJSON<AppLicenceDataList>(getLicenceSettingsURL(REQUIRED_LICENCE_VALUES), {
        cacheTime: LICENCE_FILE_CACHE_TIME
    })

export const getLicenceValues = (): Promise<Nullable<LicenceValue[]>> =>
    getLicenceSettings().then(
        (licenceData: JSONResponse<AppLicenceDataList>): Nullable<LicenceValue[]> => licenceData?.Data?.Items
    )

export const getLicenceValue = (name: string): Promise<Nullable<LicenceValue>> =>
    getLicenceValues().then(
        (licenceValues: Nullable<LicenceValue[]>): Nullable<LicenceValue> =>
            licenceValues?.find((licenceValue: LicenceValue): boolean => licenceValue?.Name === name)
    )

/**
 * Get the list of TelSysProfiles from the appLicence.xml file.
 *
 * @returns List of telsys profiles.
 */
export const getTelSysProfiles = (): Promise<Nullable<TelSysProfile<LicenceValues>[]>> =>
    getLicenceSettings().then(
        (licenceData: JSONResponse<AppLicenceDataList>): Nullable<TelSysProfile<LicenceValues>[]> => {
            const telSysProfilesData: Nullable<TelSysProfile<LicenceValue[]>[]> = licenceData?.Data?.TelSysProfiles

            if (
                !telSysProfilesData ||
                (typeof telSysProfilesData === 'object' && Object.keys(telSysProfilesData).length === 0)
            )
                return []

            return telSysProfilesData.map(
                (telSysProfile: TelSysProfile<LicenceValue[]>): TelSysProfile<LicenceValues> => ({
                    ...telSysProfile,
                    LicenseValues: telSysProfile?.LicenseValues?.reduce(
                        (acc: LicenceValues, licenceValue: LicenceValue): LicenceValues => ({
                            ...acc,
                            [licenceValue.Name]: licenceValue.Value
                        }),
                        {}
                    ),
                    type: telSysProfile?.LicenseValues?.find(
                        (licenceValue: LicenceValue): boolean => licenceValue?.Name === TEL_SYS_PROFILE_TYPE_ID
                    )?.Value
                })
            )
        }
    )

const licenceResponseMapper = (licenceResponse: Nullable<LicenceResponse>): Nullable<LicenceResponseMapped> =>
    licenceResponse
        ? {
              MaxLicences: {
                  Essentials:
                      (licenceResponse?.MaxLicenseCap?.EssentialsCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.EssentialsCap,
                  Professional:
                      (licenceResponse?.MaxLicenseCap?.ProfessionalCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.ProfessionalCap,
                  Enterprise:
                      (licenceResponse?.MaxLicenseCap?.EnterpriseCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.EnterpriseCap,
                  CallCentreAgent:
                      (licenceResponse?.MaxLicenseCap?.CallCentreAgentCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.CallCentreAgentCap,
                  CallCentreSupervisor:
                      (licenceResponse?.MaxLicenseCap?.CallCentreSupervisorCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.CallCentreSupervisorCap,
                  CXIntegration:
                      (licenceResponse?.MaxLicenseCap?.CXIntegrationCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.CXIntegrationCap,
                  CXIData:
                      (licenceResponse?.MaxLicenseCap?.CXIDataCap ?? 1) < 0
                          ? null
                          : licenceResponse?.MaxLicenseCap?.CXIDataCap
              },
              UsedLicences: {
                  Essentials:
                      (licenceResponse?.UsedLicenseCap?.EssentialsCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.EssentialsCap,
                  Professional:
                      (licenceResponse?.UsedLicenseCap?.ProfessionalCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.ProfessionalCap,
                  Enterprise:
                      (licenceResponse?.UsedLicenseCap?.EnterpriseCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.EnterpriseCap,
                  CallCentreAgent:
                      (licenceResponse?.UsedLicenseCap?.CallCentreAgentCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.CallCentreAgentCap,
                  CallCentreSupervisor:
                      (licenceResponse?.UsedLicenseCap?.CallCentreSupervisorCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.CallCentreSupervisorCap,
                  CXIntegration:
                      (licenceResponse?.UsedLicenseCap?.CXIntegrationCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.CXIntegrationCap,
                  CXIData:
                      (licenceResponse?.UsedLicenseCap?.CXIDataCap ?? 1) < 0
                          ? null
                          : licenceResponse?.UsedLicenseCap?.CXIDataCap
              },
              AvailableLicences: {
                  Essentials:
                      (licenceResponse?.AvailableLicenseCap?.EssentialsCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.EssentialsCap,
                  Professional:
                      (licenceResponse?.AvailableLicenseCap?.ProfessionalCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.ProfessionalCap,
                  Enterprise:
                      (licenceResponse?.AvailableLicenseCap?.EnterpriseCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.EnterpriseCap,
                  CallCentreAgent:
                      (licenceResponse?.AvailableLicenseCap?.CallCentreAgentCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.CallCentreAgentCap,
                  CallCentreSupervisor:
                      (licenceResponse?.AvailableLicenseCap?.CallCentreSupervisorCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.CallCentreSupervisorCap,
                  CXIntegration:
                      (licenceResponse?.AvailableLicenseCap?.CXIntegrationCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.CXIntegrationCap,
                  CXIData:
                      (licenceResponse?.AvailableLicenseCap?.CXIDataCap ?? 1) < 0
                          ? null
                          : licenceResponse?.AvailableLicenseCap?.CXIDataCap
              },
              Unlicenced: licenceResponse?.Unlicenced ?? 0
          }
        : undefined

export const getCMELicences = (cmeID: string): Promise<Nullable<LicenceResponseMapped>> =>
    requestGetJSON<APIData<LicenceResponse>>(cmeLicencesURL(cmeID), { cacheTime: LICENCE_SUMMARY_CACHE_TIME }).then(
        (licenceResponse: JSONResponse<APIData<LicenceResponse>>): Nullable<LicenceResponseMapped> =>
            licenceResponseMapper(licenceResponse?.Data)
    )

export const getTelSysLicences = (telSysID: number | string): Promise<Nullable<LicenceResponseMapped>> =>
    requestGetJSON<APIData<LicenceResponse>>(telSysLicencesURL(telSysID), {
        cacheTime: LICENCE_SUMMARY_CACHE_TIME
    }).then(
        (licenceResponse: JSONResponse<APIData<LicenceResponse>>): Nullable<LicenceResponseMapped> =>
            licenceResponseMapper(licenceResponse?.Data)
    )

/**
 * Loads the routed licence management status payload.
 *
 * @returns Routed licence management status data.
 */
export const getLicenseManagementStatus = (): Promise<Nullable<LicenseManagementStatusResponse>> =>
    requestGetJSON<LicenseManagementStatusResponse>(licenceStatusURL, {
        cacheTime: LICENCE_MANAGEMENT_STATUS_CACHE_TIME
    })

/**
 * Triggers a server-side licence reload.
 *
 * @returns Successful API response when reload completes.
 */
export const reloadLicenseManagementStatus = (): Promise<Nullable<APIData<{ Success: boolean }>>> =>
    requestJSON<APIData<{ Success: boolean }>, null>(licenceReloadURL, {
        method: RESTMethods.PUT
    })

/**
 * Builds the download URL for one of the routed licence management report endpoints.
 *
 * @param reportType Report endpoint to target.
 * @param query Current report scope and advanced options.
 * @returns Download URL constructor.
 */
export const getLicenseManagementReportDownloadURL = (
    reportType: LicenseManagementReportType,
    query: LicenseManagementReportQueryParams
) => getLicenseManagementReportURL(reportType, query)
